import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Pencil, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ServiceCategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ServiceCategoryManager = ({ isOpen, onClose }: ServiceCategoryManagerProps) => {
  const { toast } = useToast();
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("service_categories")
      .select("*")
      .eq("user_id", user.id)
      .order("name");

    if (data) setCategories(data);
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    // Check for duplicates (case-insensitive)
    const isDuplicate = categories.some(
      cat => cat.name.toLowerCase() === newCategory.trim().toLowerCase()
    );

    if (isDuplicate) {
      toast({ 
        title: "Duplicate Revenue Source", 
        description: "This revenue source already exists.", 
        variant: "destructive" 
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("service_categories").insert({
        user_id: user.id,
        name: newCategory.trim(),
      });

      if (error) throw error;

      toast({ title: "Revenue Source Added" });
      setNewCategory("");
      fetchCategories();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add revenue source", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (category: { id: string; name: string }) => {
    setCategoryToDelete(category);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    const { error } = await supabase.from("service_categories").delete().eq("id", categoryToDelete.id);

    if (error) {
      toast({ title: "Error", description: "Failed to delete revenue source", variant: "destructive" });
    } else {
      toast({ title: "Revenue Source Deleted" });
      fetchCategories();
    }

    setDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };

  const handleEditClick = (category: { id: string; name: string }) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
  };

  const handleSaveEdit = async () => {
    if (!editingCategoryId || !editingCategoryName.trim()) {
      setEditingCategoryId(null);
      return;
    }

    // Check for duplicates (case-insensitive, excluding current category)
    const isDuplicate = categories.some(
      cat => cat.id !== editingCategoryId && 
      cat.name.toLowerCase() === editingCategoryName.trim().toLowerCase()
    );

    if (isDuplicate) {
      toast({ 
        title: "Duplicate Revenue Source", 
        description: "This revenue source already exists.", 
        variant: "destructive" 
      });
      return;
    }

    const { error } = await supabase
      .from("service_categories")
      .update({ name: editingCategoryName.trim() })
      .eq("id", editingCategoryId);

    if (error) {
      toast({ title: "Error", description: "Failed to update revenue source", variant: "destructive" });
    } else {
      toast({ title: "Revenue Source Updated" });
      fetchCategories();
      setEditingCategoryId(null);
      setEditingCategoryName("");
    }
  };

  const handleCancelEdit = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Revenue Sources</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <form onSubmit={handleAddCategory} className="flex gap-2">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="e.g., Web Development, SEO Consulting, Design Services"
              disabled={loading}
            />
            <Button type="submit" disabled={loading || !newCategory.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </form>

          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Existing Revenue Sources</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                editingCategoryId === cat.id ? (
                  <div key={cat.id} className="flex items-center gap-1">
                    <Input
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit();
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                      className="h-7 text-sm w-40"
                      autoFocus
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={handleSaveEdit}
                      className="h-7 w-7"
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={handleCancelEdit}
                      className="h-7 w-7"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <Badge key={cat.id} variant="secondary" className="gap-2 pr-1">
                    {cat.name}
                    <button
                      onClick={() => handleEditClick({ id: cat.id, name: cat.name })}
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick({ id: cat.id, name: cat.name })}
                      className="hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              ))}
              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground">No revenue sources yet. Add your first offering above.</p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Revenue Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{categoryToDelete?.name}</strong>"? 
              This action cannot be undone and may affect existing invoices and leads.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCategoryToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
